import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Code, Copy, Check, Loader2, Info, ShoppingCart, UserPlus, ShoppingBag, Target, Eye, Sparkles } from 'lucide-react';
import { DashboardLayout } from '@/components/layout';
import { Card, CardTitle, Badge } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useStats } from '@/hooks/useStats';

const Pixel = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { stats, loading } = useStats('month');
  const [copied, setCopied] = useState(false);

  const snippet = `<script src="https://klipr.it/pixel.js" data-klipr-id="${user?.uid || 'YOUR_USER_ID'}"></script>`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const eventExamples = [
    {
      type: 'purchase',
      label: t('pixel.eventPurchase'),
      icon: <ShoppingCart className="w-4 h-4" />,
      code: "klipr.track('purchase', { value: 29.99, currency: 'EUR' });",
      color: 'bg-green-100 text-green-600',
    },
    {
      type: 'signup',
      label: t('pixel.eventSignup'),
      icon: <UserPlus className="w-4 h-4" />,
      code: "klipr.track('signup');",
      color: 'bg-blue-100 text-blue-600',
    },
    {
      type: 'add_to_cart',
      label: t('pixel.eventAddToCart'),
      icon: <ShoppingBag className="w-4 h-4" />,
      code: "klipr.track('add_to_cart', { value: 49.99, product: 'Widget Pro' });",
      color: 'bg-orange-100 text-orange-600',
    },
    {
      type: 'lead',
      label: t('pixel.eventLead'),
      icon: <Target className="w-4 h-4" />,
      code: "klipr.track('lead');",
      color: 'bg-purple-100 text-purple-600',
    },
    {
      type: 'page_view',
      label: t('pixel.eventPageView'),
      icon: <Eye className="w-4 h-4" />,
      code: "klipr.track('page_view');",
      color: 'bg-gray-100 text-gray-600',
    },
    {
      type: 'custom',
      label: t('pixel.eventCustom'),
      icon: <Sparkles className="w-4 h-4" />,
      code: "klipr.track('custom', { action: 'video_play' });",
      color: 'bg-lime-100 text-lime-600',
    },
  ];

  const conversionStats = useMemo(() => {
    if (!stats) return null;
    return {
      totalConversions: stats.totalConversions ?? 0,
      conversionRate: stats.conversionRate ?? 0,
      totalRevenue: stats.totalRevenue ?? 0,
      avgOrderValue: stats.avgOrderValue ?? 0,
      conversionsByType: stats.conversionsByType ?? [],
      conversionsByDay: stats.conversionsByDay ?? [],
      recentConversions: stats.recentConversions ?? [],
    };
  }, [stats]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const formatDate = (timestamp: string) => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp));
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 mb-2">{t('pixel.title')}</h1>
        <p className="text-gray-500">{t('pixel.subtitle')}</p>
      </div>

      {/* Installation Section */}
      <Card variant="default" padding="lg" className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
            <Code className="w-5 h-5 text-lime-600" />
          </div>
          <CardTitle>{t('pixel.setup')}</CardTitle>
        </div>
        <p className="text-gray-500 mb-4">{t('pixel.setupDescription')}</p>
        <div className="relative">
          <pre className="bg-gray-900 text-gray-100 rounded-2xl p-4 pr-14 text-sm overflow-x-auto">
            <code>{snippet}</code>
          </pre>
          <button
            onClick={handleCopy}
            className="absolute top-3 right-3 p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors text-gray-300"
            title={t('pixel.copySnippet')}
          >
            {copied ? <Check className="w-4 h-4 text-lime-400" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        {copied && (
          <p className="text-sm text-lime-600 mt-2">{t('pixel.snippetCopied')}</p>
        )}
      </Card>

      {/* How it works */}
      <Card variant="default" padding="lg" className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-lavender-100 flex items-center justify-center">
            <Info className="w-5 h-5 text-lavender-600" />
          </div>
          <CardTitle>{t('pixel.howItWorks')}</CardTitle>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0 font-semibold text-dark-800 text-sm">
                {step}
              </div>
              <p className="text-sm text-gray-600">{t(`pixel.step${step}`)}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Event Types Documentation */}
      <Card variant="default" padding="lg" className="mb-6">
        <CardTitle className="mb-2">{t('pixel.events')}</CardTitle>
        <p className="text-gray-500 text-sm mb-4">{t('pixel.eventsDescription')}</p>
        <div className="space-y-3">
          {eventExamples.map((ev) => (
            <div key={ev.type} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${ev.color}`}>
                {ev.icon}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-dark-800">{ev.label}</p>
                <code className="text-xs text-gray-500 block truncate">{ev.code}</code>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-lavender-500 animate-spin" />
        </div>
      )}

      {!loading && conversionStats && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card variant="default" padding="md">
              <p className="text-2xl font-bold text-dark-800">
                {conversionStats.totalConversions.toLocaleString(i18n.language)}
              </p>
              <p className="text-sm text-gray-500">{t('pixel.totalConversions')}</p>
            </Card>
            <Card variant="default" padding="md">
              <p className="text-2xl font-bold text-dark-800">
                {conversionStats.conversionRate}%
              </p>
              <p className="text-sm text-gray-500">{t('pixel.conversionRate')}</p>
            </Card>
            <Card variant="default" padding="md">
              <p className="text-2xl font-bold text-dark-800">
                {formatCurrency(conversionStats.totalRevenue)}
              </p>
              <p className="text-sm text-gray-500">{t('pixel.totalRevenue')}</p>
            </Card>
            <Card variant="default" padding="md">
              <p className="text-2xl font-bold text-dark-800">
                {formatCurrency(conversionStats.avgOrderValue)}
              </p>
              <p className="text-sm text-gray-500">{t('pixel.avgOrderValue')}</p>
            </Card>
          </div>

          {/* Conversions by Type */}
          {conversionStats.conversionsByType.length > 0 && (
            <Card variant="default" padding="lg" className="mb-6">
              <CardTitle className="mb-4">{t('pixel.conversionsByType')}</CardTitle>
              <div className="flex flex-wrap gap-3">
                {conversionStats.conversionsByType.map((item) => (
                  <div key={item.type} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl">
                    <Badge variant="lavender" size="sm">{item.type}</Badge>
                    <span className="font-semibold text-dark-800">{item.count.toLocaleString(i18n.language)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recent Conversions Table */}
          <Card variant="default" padding="none">
            <div className="p-6 border-b border-gray-100">
              <CardTitle>{t('pixel.recentConversions')}</CardTitle>
            </div>
            {conversionStats.recentConversions.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-lavender-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Code className="w-8 h-8 text-lavender-600" />
                </div>
                <h3 className="text-lg font-semibold text-dark-800 mb-2">{t('pixel.noConversionsYet')}</h3>
                <p className="text-gray-500 max-w-md mx-auto text-sm">{t('pixel.noConversionsMessage')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                      <th className="px-6 py-3 font-medium">{t('pixel.eventType')}</th>
                      <th className="px-6 py-3 font-medium">{t('pixel.value')}</th>
                      <th className="px-6 py-3 font-medium">{t('pixel.attribution')}</th>
                      <th className="px-6 py-3 font-medium">{t('pixel.date')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {conversionStats.recentConversions.map((conv) => (
                      <tr key={conv.conversionId} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3">
                          <Badge variant="lavender" size="sm">{conv.eventType}</Badge>
                        </td>
                        <td className="px-6 py-3 font-medium text-dark-800">
                          {conv.eventValue != null ? formatCurrency(conv.eventValue) : '—'}
                        </td>
                        <td className="px-6 py-3">
                          <Badge
                            variant={conv.attributionMethod === 'click_id' ? 'lime' : 'dark'}
                            size="sm"
                          >
                            {conv.attributionMethod}
                          </Badge>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-500">
                          {formatDate(conv.timestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </DashboardLayout>
  );
};

export default Pixel;
